import type { ReactNode } from "react";
import { emptyItemDraft, type ItemDraft } from "../../lib/billing-items";
import type { Product, Tax } from "../../lib/billing-types";
import { useConfig } from "../../state/config-context";

const MAX_ITEMS = 200;

export function ItemsEditor({
  items,
  products,
  taxes,
  busy,
  onChange,
}: {
  items: ItemDraft[];
  products: Product[];
  taxes: Tax[];
  busy: boolean;
  onChange: (next: ItemDraft[]) => void;
}): ReactNode {
  const { t } = useConfig();

  const update = (index: number, patch: Partial<ItemDraft>): void => {
    onChange(items.map((row, position) => (position === index ? { ...row, ...patch } : row)));
  };

  const onProductChange = (index: number, productId: string): void => {
    const product = productId === "" ? undefined : products.find((row) => row.id === productId);
    if (product === undefined) {
      update(index, { product_id: "", tax_id: "" });
      return;
    }
    update(index, {
      product_id: product.id,
      description: product.description ?? product.name,
      unit_price: (Number.parseInt(product.unit_price_minor, 10) / 100).toFixed(2),
      tax_id: product.tax_id ?? "",
    });
  };

  const remove = (index: number): void => {
    onChange(items.filter((_, position) => position !== index));
  };

  const add = (): void => {
    onChange([...items, emptyItemDraft(`item-${String(Date.now())}-${String(items.length)}`)]);
  };

  return (
    <div className="billing-items">
      <div className="table-wrap">
        <table className="table">
          <caption className="sr-only">{t("billing.quote.items")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("billing.item.product")}</th>
              <th scope="col">{t("billing.item.description")}</th>
              <th scope="col">{t("billing.item.quantity")}</th>
              <th scope="col">{t("billing.item.unitPrice")}</th>
              <th scope="col">{t("billing.item.discount")}</th>
              <th scope="col">{t("billing.item.tax")}</th>
              <th scope="col" className="table__actions-col">
                <span className="sr-only">{t("billing.common.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.key}>
                <td>
                  <select
                    aria-label={t("billing.item.product")}
                    value={item.product_id}
                    disabled={busy}
                    onChange={(event) => onProductChange(index, event.target.value)}
                  >
                    <option value="">{t("billing.item.productNone")}</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={t("billing.item.description")}
                    value={item.description}
                    maxLength={2000}
                    required
                    disabled={busy}
                    onChange={(event) => update(index, { description: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={t("billing.item.quantity")}
                    inputMode="decimal"
                    value={item.quantity}
                    required
                    disabled={busy}
                    onChange={(event) => update(index, { quantity: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={t("billing.item.unitPrice")}
                    inputMode="decimal"
                    value={item.unit_price}
                    required
                    disabled={busy}
                    onChange={(event) => update(index, { unit_price: event.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={t("billing.item.discount")}
                    inputMode="decimal"
                    value={item.discount_percent}
                    disabled={busy}
                    onChange={(event) => update(index, { discount_percent: event.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label={t("billing.item.tax")}
                    value={item.tax_id}
                    disabled={busy}
                    onChange={(event) => update(index, { tax_id: event.target.value })}
                  >
                    <option value="">{t("billing.item.taxNone")}</option>
                    {taxes.map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="table__actions-col">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    onClick={() => remove(index)}
                  >
                    {t("billing.item.remove")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="inline-actions">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={busy || items.length >= MAX_ITEMS}
          onClick={add}
        >
          {t("billing.item.add")}
        </button>
      </div>
    </div>
  );
}
